
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import moment from "moment";
import { TicketDto } from "../../dtos/ticketDto";
import DatePicker from "../../components/DatePicker.vue";
import DefineGebindeComponent from "./DefineGebindeComponent.vue";
import { BlexonResolutionType } from "../../dtos/blexonResolutionType";
import { TransportProviderDto } from "../../dtos/transportProviderDto";
import rules from "@/store/rules";
import { TransportCalculationDto } from "@/dtos/transportCalculationDto";

@Component({
	components: {
		"date-picker": DatePicker,
		DefineGebindeComponent,
	},
})
export default class TransportSettings extends Vue {
	@Prop()
	private ticketDto!: TicketDto;

	private transportCostCalculating: boolean = false;
	private transportCostCalculationMessage: string = "";
	private validForm: boolean = true;
	private errorMessage: string = "";
	private transportProviders: TransportProviderDto[] = new Array();
	private transportCalculation: TransportCalculationDto | null = null;
	private returnValid: boolean = false;

	private gebindeOpen: boolean = false;
	private requiredInteger0To100Rule = rules.requiredInteger0To100Rule;
	private requiredNonNegativeNumberRule = rules.requiredNonNegativeNumberRule;

	// After creating a Nacharbeit Rücktransport, the tracking link is shown
	private trackingLink: string = "";

	private async mounted() {
		await this.loadTransportProviders();
		await this.loadTransportSettings().then(this.getTrackingLink);
	}

	@Watch("returnValid")
	private onReturnValidChanged() {
		this.$emit("returnValidChanged", this.returnValid);
	}

	private get replacementTransportProviders() {
		return this.transportProviders.filter(
			(t) => t.weightLimit > this.totalWeightOfReplacementParts
		);
	}

	private async loadTransportProviders() {
		const response = await httpClient().get(
			`Ticket/GetTransportProviders?ticketId=${this.ticketDto.id}`
		);
		this.transportProviders = response.data;
		this.transportProviders.push({
			name: "Rampenabholung",
			id: 0,
			weightLimit: 2000000,
		});
	}

	private async loadTransportSettings() {
		try {
			this.transportCostCalculating = true;
			const response = await httpClient().get(
				`Ticket/GetReturnTransportSettings?ticketId=${this.ticketDto.id}`
			);
			this.transportCalculation = response.data;
			this.$emit(
				"transportExistsChanged",
				this.transportCalculation!.hasDeliveryNote || this.isRampenabholungSelected
			);
			this.$emit("returnValidChanged", this.returnValid);
		} finally {
			this.transportCostCalculating = false;
		}
	}

	private async updateTransportSettings() {
		try {
			this.transportCostCalculating = true;
			await httpClient().post(
				`Ticket/UpdateReturnTransportSettings?ticketId=${this.ticketDto.id}`,
				this.transportCalculation
			);
			this.$emit(
				"transportExistsChanged",
				this.transportCalculation!.hasDeliveryNote || this.isRampenabholungSelected
			);
			this.$emit("returnValidChanged", this.returnValid);
		} finally {
			this.transportCostCalculating = false;
		}
	}

	private checkIfValidAndOpenGebinde() {
		if (this.isTransportValid) {
			this.gebindeOpen = true;
			this.errorMessage = "";
		} else {
			this.errorMessage =
				"Kein Transportprovider ausgewählt. Bei Rampenabholung muss kein Transport beauftragt werden.";
		}
	}

	private onCreatedDelivery() {
		this.gebindeOpen = false;
		this.transportCalculation!.hasDeliveryNote = true;
		this.$emit("transportExistsChanged", true);
		this.getTrackingLink();
		this.downloadDelivery();
	}

	private async getTrackingLink() {
		if (this.transportCalculation && this.transportCalculation.hasDeliveryNote) {
			try {
				const response = await httpClient().get(
					`Ticket/GetDeliveryTrackingLink?ticketId=${this.ticketDto.id}`
				);
				this.trackingLink = response.data;
			} catch (ex) {
				console.log("Tracking-Link konnte nicht abgefragt werden", ex);
			}
		} else {
			console.log("There is no delivery note so tracking link is not fetched.");
		}
	}

	private async deleteReturnDelivery() {
		try {
			this.transportCostCalculating = true;
			await httpClient().post(
				`Ticket/DeleteReturnDeliveryNote?ticketId=${this.ticketDto.id}`
			);
			if (this.transportCalculation) {
				this.transportCalculation.hasDeliveryNote = false;
				this.$emit("transportExistsChanged", this.isRampenabholungSelected);
			}
		} finally {
			this.transportCostCalculating = false;
		}
	}

	private async downloadDelivery() {
		try {
			this.transportCostCalculating = true;
			this.loadTransportSettings();
			const noteResponse = await httpClient().get(
				`Ticket/DownloadDeliveryNote?ticketId=${this.ticketDto.id}`,
				{
					responseType: "blob",
				}
			);
			const url = window.URL.createObjectURL(new Blob([noteResponse.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "Rücksendeschein " + this.ticketDto.orderId + ".pdf");
			link.click();

			const numberResponse = await httpClient().get(
				`Ticket/GetNumberOfLabels?ticketId=${this.ticketDto.id}`
			);
			let numberOfLabels = numberResponse.data;

			for (let index = 0; index < numberOfLabels; index++) {
				const labelResponse = await httpClient().get(
					`Ticket/DownloadLabel?ticketId=${this.ticketDto.id}&index=${index}`,
					{
						responseType: "blob",
					}
				);
				if (labelResponse.data.size > 0) {
					const url = window.URL.createObjectURL(new Blob([labelResponse.data]));
					const link = document.createElement("a");
					link.href = url;
					link.setAttribute("download", `Label ${this.ticketDto.orderId}-${index}.pdf`);
					link.click();
				}
			}
		} finally {
			this.transportCostCalculating = false;
		}
	}

	private checkValid() {
		console.log("checking valid");
		this.$emit("returnValidChanged", this.returnValid);
	}

	private get isTransportValid() {
		return (
			this.transportCalculation &&
			this.transportCalculation.transportProviderId &&
			this.transportCalculation.transportProviderId > 0
		);
	}

	private get isRampenabholungSelected() {
		return this.transportCalculation!.transportProviderId === 0;
	}

	private get totalWeightOfReplacementParts() {
		const relevantComplaints = this.ticketDto.complains.filter(
			(c) =>
				c.blexonResolution == BlexonResolutionType.AcceptedNacharbeit ||
				c.blexonResolution == BlexonResolutionType.AcceptedErsatzlieferung
		);
		let totalWeight: number = 0;
		relevantComplaints.forEach((complain) => {
			totalWeight += complain.part.gewicht * complain.numberOfWrongParts;
		});
		return totalWeight;
	}

	private get totalWeightOfNacharbeitParts() {
		const relevantComplaints = this.ticketDto.complains.filter(
			(c) => c.blexonResolution == BlexonResolutionType.AcceptedNacharbeit
		);
		let totalWeight: number = 0;
		relevantComplaints.forEach((element) => {
			totalWeight += element.part.gewicht * element.part.menge;
		});
		return totalWeight;
	}

	private get dueDate() {
		var date = new Date();
		if (this.transportCalculation != null) {
			date = this.transportCalculation.dueDate;
		}
		return moment(date).format("YYYY-MM-DD");
	}
}
